<template>
    <b-container fluid class="px-3">
        <b-row>
            <b-col>
                <l-map v-if="loaded" :zoom="zoom" :center="center" ref="map" style="height: 20rem">
                    <l-tile-layer :url="url" :attribution="attribution"></l-tile-layer>
                    <div class="custom-asset-legend">
                        <div class="legend-item">
                            <div class="legend-color" style="background-color: red;"></div>
                            <div class="legend-label">{{ $translate('overdue_next_serivice_date') }}</div>
                        </div>
                        <div class="legend-item">
                            <div class="legend-color" style="background-color: #FFBF00;"></div>
                            <div class="legend-label">{{ $translate('within_month_next_serivice_date') }} </div>
                        </div>
                        <div class="legend-item">
                            <div class="legend-color" style="background-color: green;"></div>
                            <div class="legend-label">{{ $translate('after_month_next_serivice_date') }}</div>
                        </div>
                        <!-- Add more legend items as needed -->
                    </div>
                    <l-control class="map-custom-control">
                        <dashboard-filters key="asset" :title="$translate('assets')" :filters="['assetType', 'assetSearch']"
                            :asset-types="assetTypesOptions" @asset-search="assetSearch = $event"
                            @select-asset-type="assetTypeId = $event" />
                       
                    </l-control>
                    <v-marker-cluster :options="clusterOptions">
                        <l-marker v-for="(asset, index) in formattedAssets" :key="index"
                            :lat-lng="formattedAssets[index].latlng" :radius="asset.radius" :color="asset.next_service_date"
                            :fill-color="asset.next_service_date" style="z-index: 5000 !important"
                            :icon="assetIcon(asset.next_service_date)">


                            <l-popup :autoClose="false">
                                <span class="popup_header">{{ `${asset.name} (${asset.schedule_name})` }}</span><br />
                                <span class="popup_label">{{ $translate('customer') }}:</span>{{ asset.customer_name
                                }}<br />
                                <span class="popup_label">{{ $translate('next_service') }}:</span>{{ asset.next_service_date
                                }}<br />
                                <span class="popup_label">{{ $translate('type') }}:</span>{{ asset.type_name }}<br />
                                <span class="popup_label">{{ $translate('serial_number') }}:</span>{{ asset.serial_no
                                }}<br />
                                <a :href="formatPath(asset)">{{ $translate('view_details') }}</a>
                            </l-popup>
                        </l-marker>
                    </v-marker-cluster>
                    <l-geo-json :geojson="geojson"></l-geo-json>
                </l-map>
            </b-col>
        </b-row>
    </b-container>
</template>

<script>
import DashboardFilters from '@/views/Dashboard/DashboardFilters'
import {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LGeoJson,
    LControl,
} from 'vue2-leaflet'
import L from 'leaflet'
import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster'

const defaultLocation = L.latLng(18, -77.2)

export default {
    name: 'assets-map',
    components: {
        'v-marker-cluster': Vue2LeafletMarkerCluster,
        LMap,
        LTileLayer,
        LMarker,
        LPopup,
        LGeoJson,
        LControl,
        DashboardFilters,
    },
    props: {
        assets: {
            type: Array,
            default: () => [],
        },
        assetTypes: {
            type: Array,
            default: () => [],
        },
        value: {
            type: Object,
            default: () => defaultLocation,
        },
        loaded: {
            type: Boolean,
            default: () => false,
        },
        geojson: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            assetTypeId: null,
            assetSearch: '',
            zoom: 8,
            url: 'https://{s}.tile.osm.org/{z}/{x}/{y}.png',
            attribution:
                '&copy; <a href="https://osm.org/copyright">OpenStreetMap</a> contributors',
            clusterOptions:{
                iconCreateFunction: cluster => {
                    var childCount = cluster.getChildCount();
                    var c = " marker-cluster-";
                    if (childCount < 10) {
                        c += "small";
                    } else if (childCount < 100) {
                        c += "medium";
                    } else {
                        c += "large";
                    }
                    let className={
                        red: 0,
                        amber: 0,
                        green: 0
                    }

                    // Assuming your data is stored in cluster.getAllChildMarkers()
                    const clusterMarkers = cluster.getAllChildMarkers();

                    clusterMarkers.forEach(marker => {

                        const next_service_date = marker.options.icon.options.next_service_date;

                        const dateDiff = xdate =>
                        (new Date(xdate).getTime() - Date.now()) /
                        (1000 * 60 * 60 * 24 * 30)
                        const ddiff = dateDiff(next_service_date)
                        
                        if (ddiff > 1) className.green += 1;
                        else if (ddiff > 0) className.amber += 1;
                        else className.red += 1;
                    });
                    let iconColor;
                    // lower priority asset next visit count
                    if(className.green > 0){
                        iconColor='green-cluster'
                    }
                    // higher priority asset next visit count
                    if(className.amber > 0){
                        iconColor='amber-cluster'
                    }
                    // highest priority asset next visit count
                    if(className.red > 0){
                        iconColor='red-cluster'
                    }
                    return new L.DivIcon({
                        html: "<div><span>" + childCount + "</span></div>",
                        className: `marker-cluster ${c} ${iconColor}`,
                        iconSize: new L.Point(40, 40)
                    });
                },
            }
        }
    },
    computed: {
        crmSettingValue() {
            return this.$store.getters['company/crmSettingValue']
        },
        assetTypesOptions() {
            return this.assetTypes.map(x => ({
                value: x.id,
                text: x.name,
            }))
        },

        formattedAssets() {
            const { assetSearch, assetTypeId } = this
            const assets = this.getAssets(assetSearch, assetTypeId)

            return assets.map(_asset => {
                _asset.radius = 8

                const { latitude, longitude } = this.setCoordinate(_asset)

                _asset.latlng = new L.latLng(latitude, longitude)

                return _asset
            })
        },
        center() {
            const count = this.formattedAssets.length
            if (count === 0) return defaultLocation
            const s = this.formattedAssets.reduce(
                (sum, asset) => {
                    const { latitude, longitude } = this.setCoordinate(asset)
                    sum.latitude += latitude
                    sum.longitude += longitude
                    return sum
                },
                {
                    latitude: 0,
                    longitude: 0,
                }
            )
            let latitude = 0,
                longitude = 0
            if (count > 0) {
                latitude = s.latitude / count
                longitude = s.longitude / count
            }
            latitude = this.$store.state.company.settings.latitude
            longitude = this.$store.state.company.settings.longitude
            return new L.latLng(latitude, longitude)
        },
    },
    watch: {
        loaded: function (val) {
            if (val) {
                const $this = this
                this.$nextTick(() => {
                    const map = $this.$refs.map.mapObject
                    map.addControl(new L.Control.Fullscreen())
                })
            }
        },
    },

    mounted() {
        this.$store.dispatch('company/fetch')
        console.log('assets-mounted', this.assets)
    },
    methods: {
        formatPath(asset) {
            if (asset.type_id == this.crmSettingValue.type_id) {
                return `/#/ams/crm/${asset.id}`
            }
            return `/#/ams/assets/${asset.id}`

        },
        setCoordinate({ longitude, latitude }) {
            longitude = parseFloat(longitude) || -77.2
            latitude = parseFloat(latitude) || 18
            return { longitude, latitude }
        },
        assetIcon(next_service_date) {
            const dateDiff = xdate =>
                (new Date(xdate).getTime() - Date.now()) /
                (1000 * 60 * 60 * 24 * 30)
            const ddiff = dateDiff(next_service_date)
            let iconColor;
            if (ddiff > 1) {
                iconColor = 'green';
            } else if (ddiff > 0) {
                iconColor = '#FFBF00';
            } else {
                iconColor = 'red';
            }
            return L.divIcon({
                html: `<i class="fas fa-tools fa-2x" style="color: ${iconColor};"></i>`,
                next_service_date
                // iconSize: [100, 100], // Adjust the size of the icon as needed
                // You can customize other icon properties here, such as iconAnchor, popupAnchor, etc.
            });
        },
        getAssets(assetSearch, type_id) {
            if (!assetSearch && !type_id) return this.assets
            const x_search = (assetSearch || '').trim().toLowerCase()
            return this.assets.filter(
                x =>
                    (!x_search ||
                        x.customer_name.toLowerCase().indexOf(x_search) >= 0 ||
                        x.name.toLowerCase().indexOf(x_search) >= 0 || 
                        x.schedule_name.toLowerCase().indexOf(x_search) >= 0) &&
                    (!type_id || type_id === x.type_id)
            )
        },
    },
}
</script>

<style>
.autocomplete input {
    border-radius: 0;
}

#map.fullscreen {
    z-index: 9999;
    width: 100%;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
}

#map.leaflet-bottom {
    z-index: 1 !important;
}

#map {
    height: 418px;
    z-index: 0 !important;
}

#map.leaflet-control {
    z-index: 0 !important;
}

.map-custom-control {
    background: #fff;
    padding: 0 0.5em;
    border: 1px solid #aaa;
    border-radius: 0.1em;
    z-index: 1 !important;
}

dl {
    display: grid;
    grid-template-columns: max-content auto;
}

dt {
    grid-column-start: 1;
}

dd {
    grid-column-start: 2;
}

.popup_header {
    text-decoration: underline;
    font-weight: bold;
}

.popup_label {
    font-weight: bold;
}
.custom-asset-legend {
    position: absolute;
    bottom: 10px; /* Adjust the position as needed */
    left: 10px;   /* Adjust the position as needed */
    background-color: rgba(255, 255, 255, 0.8);
    padding: 10px;
    border: 1px solid #ccc;
    border-radius: 5px;
    z-index: 1000;
  }
  
  .legend-item {
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }
  
  .legend-color {
    width: 20px;
    height: 20px;
    margin-right: 5px;
  }
  
  .legend-label {
    font-size: 14px;
  }
</style>